import { autoUpdate, offset, useFloating } from '@floating-ui/react';
import cx from 'classnames';
import type { FC, ReactNode } from 'react';

import { Color, type IconName } from '@zen/Styleguide';

import Icon from '../Icon';
import Portal from '../Portal';
import { isColorBasic, variantClassNames } from './helpers';

export interface Props {
  animationEnabled?: boolean;
  color: Color | string;
  icon: IconName;
  iconClassName?: string;
  label?: string;
  showBorder?: boolean;
  variant?: 'default' | 'compact';
}

const IconCircle: FC<Props> = (props) => {
  const { animationEnabled = false, color, icon, iconClassName, label, showBorder = false, variant = 'default' } = props;

  const { refs, floatingStyles } = useFloating({
    middleware: [offset(variant === 'compact' ? -16 : -24)],
    placement: 'right',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate
  });
  const isBasicColor: boolean = isColorBasic(color);
  const backgroundColor: string | undefined = !isBasicColor ? undefined : color;
  const wrapperClassName: string = variantClassNames[variant];
  const classNames: string = cx(wrapperClassName, 'rounded-full flex items-center justify-center', { [color]: !isBasicColor });

  const circleContent: ReactNode = (
    <div className={classNames} data-testid="icon-circle" style={{ backgroundColor }}>
      <Icon className={iconClassName} icon={icon} />
    </div>
  );

  const circle: ReactNode = showBorder ? (
    <div className="inline-flex rounded-full border-2 border-white">{circleContent}</div>
  ) : (
    circleContent
  );

  if (!animationEnabled) {
    return circle;
  }

  const circleClassNames: string = cx(
    'p-[5px] rounded-full flex items-center space-x-1 overflow-hidden group-hover/icon-circle:max-w-[200px] transition-[max-width,z-index] duration-150 ease-linear z-0 group-hover/icon-circle:z-10',
    {
      'max-w-6': variant === 'default'
    }
  );

  return (
    <>
      <div ref={refs.setReference} className={wrapperClassName} />

      <Portal>
        <div className="relative group/icon-circle">
          <div
            ref={refs.setFloating}
            className={circleClassNames}
            data-testid="icon-circle"
            style={{ backgroundColor: color, ...floatingStyles }}
          >
            <Icon className={iconClassName} icon={icon} />
            {animationEnabled && (
              <span className="group-hover/icon-circle:opacity-100 opacity-0 transition-opacity duration-150 text-sm whitespace-nowrap font-normal antialiased">
                {label}
              </span>
            )}
          </div>
        </div>
      </Portal>
    </>
  );
};

export default IconCircle;
